import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { getSettings } from '../../services/api';
import EmberField from '../hero/EmberField';
import IrisTransition from '../hero/IrisTransition';
import useReveal from '../../hooks/useReveal';

const VIDEO_RE = /\.(mp4|webm|mov)(\?.*)?$/i;

const ReelCard = ({ src, index, onOpen }) => {
  const ref = useRef(null);
  return (
    <button
      className="xp-reel-card"
      data-reveal
      style={{ '--reveal-delay': `${index * 130}ms` }}
      onClick={() => onOpen(src)}
      onMouseEnter={() => ref.current?.play().catch(() => {})}
      onMouseLeave={() => {
        if (ref.current) {
          ref.current.pause();
          ref.current.currentTime = 0;
        }
      }}
      data-testid={`reel-item-${index}`}
      data-gas
      aria-label={`Play video ${index + 1}`}
    >
      <video ref={ref} src={src} muted loop playsInline preload="metadata" />
      <span className="xp-reel-play" aria-hidden="true"></span>
    </button>
  );
};

const VideoReel = () => {
  const [videos, setVideos] = useState([]);
  const [openSrc, setOpenSrc] = useState(null);
  const [iris, setIris] = useState(null);

  useEffect(() => {
    getSettings()
      .then((s) => setVideos((s.hero_images || []).filter((u) => VIDEO_RE.test(u))))
      .catch(() => {});
  }, []);

  useReveal([videos]);

  useEffect(() => {
    if (!openSrc) return;
    const onKey = (e) => e.key === 'Escape' && setOpenSrc(null);
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [openSrc]);

  const openVideo = (src) => {
    setOpenSrc(src);
    setIris('opening');
    setTimeout(() => setIris(null), 850);
  };

  if (!videos.length) return null;

  return (
    <section className="xp-reel" id="motion" data-testid="video-reel-section">
      <EmberField count={34} />
      <div className="xp-reel-inner">
        <h2 className="xp-reel-title" data-reveal>In Motion</h2>
        <p className="xp-reel-subtitle" data-reveal style={{ '--reveal-delay': '120ms' }}>
          Stories that move — a reel of moments captured in motion.
        </p>
        <div className="xp-reel-strip" data-testid="reel-strip">
          {videos.map((src, i) => (
            <ReelCard key={src} src={src} index={i} onOpen={openVideo} />
          ))}
        </div>
      </div>

      {openSrc && (
        <div className="xp-reel-lightbox" data-testid="reel-lightbox" onClick={() => setOpenSrc(null)}>
          <button
            className="xp-overlay-close"
            onClick={() => setOpenSrc(null)}
            data-testid="reel-lightbox-close"
            aria-label="Close video"
          >
            <X size={26} />
          </button>
          <video
            src={openSrc}
            autoPlay
            controls
            playsInline
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <IrisTransition phase={iris} />
    </section>
  );
};

export default VideoReel;
